import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { QuantityStepper, money } from './QuantityStepper';
import { ArrowRightIcon, CheckIcon, BuildingIcon } from 'lucide-react';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { companyPack } from '../data/product';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { trackCustom, packEvents } from '../utils/tracking';
export function CompanyPack() {
  const [quantity, setQuantity] = useState(1);
  const viewRef = useTrackOnView<HTMLElement>('business_pack_viewed');
  return <section ref={viewRef} id="company" aria-labelledby="company-pack-heading" className="mt-10 scroll-mt-28 border-2 border-grit-500 bg-ink-950 lg:mt-12" data-id="element-75" data-mp="2wg9uxfk48zbi">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-grit-500/40 bg-grit-500 px-5 py-3 text-ink-950 sm:px-8" data-id="element-76" data-mp="bqtxi1qe6ccw">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em]" data-id="element-77" data-mp="25a7tvc2eznf3">
          <BuildingIcon className="h-4 w-4" aria-hidden="true" data-id="element-78" data-mp="2a89dby3r5or3" />
          {companyPack.topBadge}
        </span>
        <span className="border border-ink-950 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-79" data-mp="2ec7f6gkgpdnu">
          {companyPack.secondaryBadge}
        </span>
      </div>

      <div className="flex flex-col gap-8 p-5 sm:p-8 lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:p-12" data-id="element-80" data-mp="1mlja4tfo3o4w">
        <div data-id="element-81" data-mp="1qmd3i2bvpv56">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-82" data-mp="1qjqv60h5zblw">{companyPack.title}</p>
          <h3 id="company-pack-heading" className="grit-headline mt-3 text-[clamp(2.75rem,9vw,5.5rem)] font-extrabold uppercase text-offwhite" data-id="element-83" data-mp="1vj3eib8tu0jq">
            Buy 20.
            <br data-id="element-84" data-mp="12uekew66uvhh" />
            <span className="text-grit-500" data-id="element-85" data-mp="17v2drohw3oev">Get 10 free.</span>
          </h3>
          <p className="mt-4 font-display text-2xl font-bold uppercase tracking-wide text-offwhite sm:text-3xl" data-id="element-86" data-mp="17szeeduukixh">
            {companyPack.units}
          </p>

          <p className="mt-8 border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl" data-id="element-87" data-mp="266dclgfp8e2v">
            {companyPack.headline}
            <span className="mt-1 block text-lg text-grit-500 sm:text-xl" data-id="element-88" data-mp="2fwpwr7jlq58h">{companyPack.subheadline}</span>
          </p>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-steel-300 sm:text-base" data-id="element-89" data-mp="1s86ogs0jxsge">
            {companyPack.copy}
          </p>

          <ul className="mt-8 grid gap-2.5 border-t border-white/10 pt-7 lg:grid-cols-2" data-id="element-90" data-mp="21821l2do3xj2">
            {companyPack.includes.map(item => <li key={item} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-91" data-mp="1w7xid4ldkop5">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-92" data-mp="3clxfy8wr0298" />
                {item}
              </li>)}
          </ul>

          <div className="mt-8 grid gap-6 border-t border-white/10 pt-7 sm:grid-cols-2" data-id="element-93" data-mp="3g4muj585l9fb">
            <div data-id="element-94" data-mp="3k6huo6eclgri">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-95" data-mp="3k6zybo3cax9g">Cost per X1</p>
              <ul className="mt-3 grid gap-px bg-white/10" data-id="element-96" data-mp="3680aujrkcoev">
                {companyPack.ladder.map(row => <li key={row.label} className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-3 py-2.5 text-[14px] ${row.best ? 'bg-grit-500 font-bold text-ink-950' : 'bg-ink-950 text-steel-200'}`} data-id="element-97" data-mp="3b6urpg5z02wc">
                    <span data-id="element-98" data-mp="3p3qeebf59suv">{row.label}</span>
                    <span className="font-display text-base font-extrabold uppercase tracking-wide" data-id="element-99" data-mp="3u4e0qjsfhlir">{row.each}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-100" data-mp="qgcf2road1xn">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-101" data-mp="qgvy6bpo9wd9">
                30 X1, three ways to buy
              </p>
              <ul className="mt-3 grid gap-px bg-white/10" data-id="element-102" data-mp="2xx2o1obmjndo">
                {companyPack.stacking.map(row => <li key={row.label} className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-3 py-2.5 text-[14px] ${row.best ? 'bg-grit-500 font-bold text-ink-950' : 'bg-ink-950 text-steel-200'}`} data-id="element-103" data-mp="2oyh880xjemgl">
                    <span data-id="element-104" data-mp="3co1sqo9c9rrz">{row.label}</span>
                    <span className="font-display text-base font-extrabold uppercase tracking-wide" data-id="element-105" data-mp="3cqek1s0zlehp">{row.total}</span>
                  </li>)}
              </ul>
              <ul className="mt-3 grid gap-1.5" data-id="element-106" data-mp="32xp70spsfsk9">
                {companyPack.stackingHighlights.map(line => <li key={line} className="font-display text-base font-bold uppercase tracking-wide text-grit-500" data-id="element-107" data-mp="49t7y2d265ad">
                    {line}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        <div className="-order-1 flex flex-col justify-center border border-white/12 bg-ink-900 p-5 sm:p-6 lg:order-none lg:p-8" data-id="element-108" data-mp="bu3t62aemui7">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-steel-400" data-id="element-109" data-mp="lpxfgddxe2d6">Your price</p>
          <p className="text-[15px] text-steel-400 line-through" data-id="element-110" data-mp="psbwcry1as1t">{money(1198.5 * quantity)}</p>
          <p className="font-display text-[clamp(3.5rem,13vw,7rem)] font-extrabold leading-none text-offwhite" data-id="element-111" data-mp="uqlt36zeq0pj">
            {money(599 * quantity)}
          </p>
          <p className="mt-3 inline-flex self-start bg-grit-500 px-4 py-2 font-display text-2xl font-extrabold uppercase leading-none text-ink-950 sm:text-3xl" data-id="element-112" data-mp="zjoo7jju1i7u">
            {companyPack.perUnit}
          </p>
          <p className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-113" data-mp="19dyg704v4ei1">
            You save {money(599.5 * quantity)} vs individual retail
          </p>
          <p className="mt-2 text-[15px] text-steel-300" data-id="element-114" data-mp="3ai91wi1a6q3p">
            {quantity > 1 ? `${quantity} packs · ${quantity * 30} X1 total.` : `${companyPack.comparison}. Business / crew price: ${companyPack.price}.`}
          </p>
          <p className="mt-3 border-t border-white/10 pt-3 text-[13px] leading-relaxed text-steel-400" data-id="element-115" data-mp="3ej2rrgyaaez8">
            Business volume pricing: 20 units paid for, 10 bonus units included. The {companyPack.price} total is a
            volume rate for crews, which is why it lands below 20 units at regular retail.
          </p>

          <QuantityStepper value={quantity} onChange={setQuantity} max={10} label="Company crew pack quantity" className="mt-6" data-id="element-116" data-mp="62hpej05qk53" />
          <a href="#offer" data-buy-intent="true" onClick={() => {
          trackCustom('business_pack_clicked', {
            quantity
          });
          trackCustom(packEvents.business.addToCart, {
            quantity,
            value: 599 * quantity
          });
        }} className="group mt-5 flex items-center justify-center gap-3 bg-grit-500 px-5 py-4 text-center font-display text-lg font-extrabold uppercase leading-tight tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:px-6 sm:py-5 sm:text-2xl" data-id="element-117" data-mp="27vxf9dof7uz0">
            {quantity > 1 ? `Outfit your crew — get ${quantity * 30} for ${money(quantity * 599)}` : companyPack.cta}
            <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform duration-150 ease-grit group-hover:translate-x-1 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-118" data-mp="113b10f4m4hpd" />
          </a>
          <p className="mt-3 text-center text-[13px] text-steel-400" data-id="element-119" data-mp="30e2b9a10vc4j">{companyPack.ctaNote}</p>

          <div className="mt-6 grid gap-3 border-t border-white/10 pt-5" data-id="element-120" data-mp="ct3xmiddho7b">
            <UspsBadge note="Free U.S. shipping." data-id="element-121" data-mp="csc4rfb5u4kq" />
            <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-colors duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-122" data-mp="7tqzv2kyqwpy">
              <GoldMedal size={92} data-id="element-123" data-mp="3q8jg2ai30thv" />
              <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-124" data-mp="3q8taqbe2notw">
                All 30 covered for life.
                <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-125" data-mp="3g9kfzgbdu66h">
                  Break one, we replace it free. Forever.
                </span>
              </p>
            </Link>
            <PrescriptionBadge compact data-id="element-126" data-mp="1ehfq0adg1iye" />
            <SecureCheckoutNote className="mt-1" data-id="element-127" data-mp="1eg4qimr16xd3" />
          </div>
        </div>
      </div>
    </section>;
}